/**
 * Шаблоны, повторы и промежуточные дела на новом бэке. Поверх
 * `entities/task/api/task-automation-v2`.
 *
 * Три подсистемы держим в одном модуле потому, что они об одном: порождают
 * задачи или дробят их. Ключи запросов у шаблонов и повторов глобальные (список
 * не привязан к задаче), у повтора и промежуточного дела конкретной задачи —
 * вложенные в её ключ.
 *
 * Оптимистики нет: состояние берётся из ответа сервера, списки инвалидируются.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getApiErrorStatus } from '@/shared/api';

import {
  completeIntermediateV2,
  createIntermediateV2,
  createTaskRecurrenceV2,
  deleteTaskRecurrenceV2,
  deleteTaskTemplateV2,
  fetchIntermediatesV2,
  fetchRecurrenceHistoryV2,
  fetchRecurrencesV2,
  fetchTaskIntermediateV2,
  fetchTaskRecurrenceV2,
  fetchTaskTemplateV2,
  fetchTaskTemplatesV2,
  instantiateTaskTemplateV2,
  pauseIntermediateV2,
  resumeIntermediateV2,
  saveTaskAsTemplateV2,
  setIntermediateTimeV2,
  updateRecurrenceV2,
  updateTaskTemplateV2,
  type IntermediateCreateV2,
  type RecurrenceCreateV2,
  type RecurrenceUpdateV2,
} from '../api/task-automation-v2';
import { tasksV2Keys } from './useTasksV2';

export const automationV2Keys = {
  templates: ['tasks-v2', 'templates'] as const,
  recurrences: ['tasks-v2', 'recurrences'] as const,
  recurrenceHistory: (id: string) =>
    ['tasks-v2', 'recurrences', id, 'history'] as const,
  intermediates: ['tasks-v2', 'intermediate'] as const,
  taskRecurrence: (taskId: string) =>
    [...tasksV2Keys.detail(taskId), 'recurrence'] as const,
  taskIntermediate: (taskId: string) =>
    [...tasksV2Keys.detail(taskId), 'intermediate'] as const,
};

/** Справочные списки живут долго — меняются редко и не из этого экрана. */
const REF_STALE = 5 * 60 * 1000;

/* --------------------------------------------------------- шаблоны --- */

export function useTaskTemplatesV2(enabled = true) {
  return useQuery({
    queryKey: automationV2Keys.templates,
    queryFn: fetchTaskTemplatesV2,
    enabled,
    staleTime: REF_STALE,
  });
}

export function useTaskTemplateActionsV2() {
  const qc = useQueryClient();
  const refresh = () =>
    qc.invalidateQueries({ queryKey: automationV2Keys.templates });

  /** Создаёт задачу по шаблону и возвращает её id. */
  const instantiate = useMutation({
    mutationFn: (templateId: string) => instantiateTaskTemplateV2(templateId),
    onSuccess: () => qc.invalidateQueries({ queryKey: tasksV2Keys.all }),
  });

  const saveAs = useMutation({
    mutationFn: (args: { taskId: string; name: string }) =>
      saveTaskAsTemplateV2(args.taskId, { name: args.name }),
    onSuccess: refresh,
  });

  /**
   * Переименование. PATCH принимает шаблон целиком (`name` обязателен), поэтому
   * сначала читаем актуальный шаблон по id и отправляем его содержимое как
   * было — иначе рискуем потерять `payload`.
   */
  const rename = useMutation({
    mutationFn: async (args: { templateId: string; name: string }) => {
      const current = await fetchTaskTemplateV2(args.templateId);
      await updateTaskTemplateV2(args.templateId, {
        name: args.name,
        ...(current.payload ? { payload: current.payload } : {}),
      });
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (templateId: string) => deleteTaskTemplateV2(templateId),
    onSuccess: refresh,
  });

  return {
    instantiate,
    saveAs,
    rename,
    remove,
    isBusy:
      instantiate.isPending ||
      saveAs.isPending ||
      rename.isPending ||
      remove.isPending,
  };
}

/* ---------------------------------------------------------- повторы --- */

export function useRecurrencesV2(enabled = true) {
  return useQuery({
    queryKey: automationV2Keys.recurrences,
    queryFn: fetchRecurrencesV2,
    enabled,
    staleTime: REF_STALE,
  });
}

export function useRecurrenceHistoryV2(
  recurrenceId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: automationV2Keys.recurrenceHistory(recurrenceId ?? ''),
    queryFn: () => fetchRecurrenceHistoryV2(recurrenceId as string),
    enabled: enabled && !!recurrenceId,
  });
}

/**
 * Правило повторения конкретной задачи. Отсутствие правила бэк отдаёт 404 —
 * это не ошибка, поэтому повторов запроса не делаем и ошибку наружу не тащим.
 */
export function useTaskRecurrenceV2(
  taskId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: automationV2Keys.taskRecurrence(taskId ?? ''),
    queryFn: () => fetchTaskRecurrenceV2(taskId as string),
    enabled: enabled && !!taskId,
    retry: false,
  });
}

export function useTaskRecurrenceActionsV2(taskId: string | undefined) {
  const qc = useQueryClient();
  const id = taskId ?? '';

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: automationV2Keys.taskRecurrence(id) }),
      qc.invalidateQueries({ queryKey: automationV2Keys.recurrences }),
    ]);
  };

  const create = useMutation({
    mutationFn: (payload: RecurrenceCreateV2) =>
      createTaskRecurrenceV2(id, payload),
    onSuccess: refresh,
  });

  const update = useMutation({
    mutationFn: (args: { recurrenceId: string; payload: RecurrenceUpdateV2 }) =>
      updateRecurrenceV2(args.recurrenceId, args.payload),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: () => deleteTaskRecurrenceV2(id),
    onSuccess: refresh,
  });

  return {
    create,
    update,
    remove,
    isBusy: create.isPending || update.isPending || remove.isPending,
  };
}

/* ---------------------------------------------- промежуточные дела --- */

/** Все незавершённые дела текущего пользователя — список глобальный. */
export function useIntermediatesV2(enabled = true) {
  return useQuery({
    queryKey: automationV2Keys.intermediates,
    queryFn: fetchIntermediatesV2,
    enabled,
  });
}

/**
 * Своё промежуточное дело по задаче. Отсутствие дела бэк отдаёт 404 — это не
 * ошибка, поэтому 404 превращаем в `null` и не повторяем запрос.
 */
export function useTaskIntermediateV2(
  taskId: string | undefined,
  enabled = true
) {
  return useQuery({
    queryKey: automationV2Keys.taskIntermediate(taskId ?? ''),
    queryFn: async () => {
      try {
        return await fetchTaskIntermediateV2(taskId as string);
      } catch (error) {
        if (getApiErrorStatus(error) === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: enabled && !!taskId,
    retry: false,
  });
}

export function useIntermediateActionsV2(taskId: string | undefined) {
  const qc = useQueryClient();
  const id = taskId ?? '';

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: automationV2Keys.intermediates }),
      qc.invalidateQueries({ queryKey: automationV2Keys.taskIntermediate(id) }),
      qc.invalidateQueries({ queryKey: tasksV2Keys.card(id) }),
    ]);
  };

  const create = useMutation({
    mutationFn: (payload: IntermediateCreateV2) =>
      createIntermediateV2(id, payload),
    onSuccess: refresh,
  });

  const pause = useMutation({
    mutationFn: () => pauseIntermediateV2(id),
    onSuccess: refresh,
  });

  const resume = useMutation({
    mutationFn: () => resumeIntermediateV2(id),
    onSuccess: refresh,
  });

  const complete = useMutation({
    mutationFn: () => completeIntermediateV2(id),
    onSuccess: refresh,
  });

  /** Правка потраченного: бэк принимает дельту в минутах, не итог. */
  const addMinutes = useMutation({
    mutationFn: (deltaMinutes: number) =>
      setIntermediateTimeV2(id, { deltaMinutes }),
    onSuccess: refresh,
  });

  return {
    create,
    pause,
    resume,
    complete,
    addMinutes,
    isBusy:
      create.isPending ||
      pause.isPending ||
      resume.isPending ||
      complete.isPending ||
      addMinutes.isPending,
  };
}
